
import { supabase } from '@/integrations/supabase/client';

export interface Sport {
  id: number;
  sport_name: string;
  created_at: string;
}

export interface SportConfiguration {
  id: string;
  sport_id: number;
  variant_name: string;
  variant_description: string | null;
  gender_category: string;
  min_players_per_team: number;
  max_players_per_team: number | null;
  total_teams: number;
  is_default_variant: boolean;
  display_format: string | null;
  field_size: string | null;
  created_at: string;
  updated_at: string;
}

class SportsService {
  async getAvailableSports(): Promise<Sport[]> {
    try {
      const { data, error } = await supabase
        .from('available_sports')
        .select('*')
        .order('sport_name');

      if (error) {
        console.error('Error fetching sports:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching sports:', error);
      return [];
    }
  }

  async getSportConfigurations(sportId?: number): Promise<SportConfiguration[]> {
    try {
      let query = supabase
        .from('sport_configurations')
        .select('*')
        .order('is_default_variant', { ascending: false })
        .order('variant_name');

      if (sportId) {
        query = query.eq('sport_id', sportId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching sport configurations:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching sport configurations:', error);
      return [];
    }
  }

  async getDefaultVariantForSport(sportId: number): Promise<SportConfiguration | null> {
    try {
      const { data, error } = await supabase
        .from('sport_configurations')
        .select('*')
        .eq('sport_id', sportId)
        .eq('is_default_variant', true)
        .single();

      if (error) {
        console.error('Error fetching default variant:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching default variant:', error);
      return null;
    }
  }

  async getSportsWithVariants(): Promise<(Sport & { variants: SportConfiguration[] })[]> {
    try {
      const { data: sports, error: sportsError } = await supabase
        .from('available_sports')
        .select('*')
        .order('sport_name');

      if (sportsError) {
        console.error('Error fetching sports:', sportsError);
        return [];
      }

      const { data: configurations, error: configError } = await supabase
        .from('sport_configurations')
        .select('*')
        .order('is_default_variant', { ascending: false })
        .order('variant_name');

      if (configError) {
        console.error('Error fetching configurations:', configError);
        return sports?.map(sport => ({ ...sport, variants: [] })) || [];
      }

      // Group configurations by sport
      const sportsWithVariants = sports?.map(sport => ({
        ...sport,
        variants: configurations?.filter(config => config.sport_id === sport.id) || []
      })) || [];

      return sportsWithVariants;
    } catch (error) {
      console.error('Error fetching sports with variants:', error);
      return [];
    }
  }
}

export const sportsService = new SportsService();
